import { takeLatest, select, call, put, all } from 'redux-saga/effects';
import {
  securityGridTableActions,
  securityFilterActions,
  securitySelectors,
  securityNS,
  securityTaskPageActions,
} from 'containers/Pages/Tasks/Security';
import { getTimezone } from 'reducers/utils';
import API from 'services/api';
import { showSnackbar, modalState, setLoading, toggleModal } from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { createSimpleTask } from 'sagas/templateSaga/templateSaga';
import { DB_NULL, PAGE_NAME } from 'utils/constants';
import isEmpty from 'lodash/isEmpty';
import { convertPercentageToimageCordinates } from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';
import { getGlobalListSaga, getGlobalUsers } from '../globalList';
import { getUserSelectedLanguage } from 'reducers/users';
import { snackbarTaskStatusColor, uploadEvidences } from './cleaningSaga';
import { translatedPerformTaskStatus } from 'utils/methods/helpers';
import { TASK_STATUS } from 'utils/constants/task';
import { t } from 'i18next';

export function* fetchSecurityListSaga({ payload }) {
  try {
    const { filters, from } = payload;
    const timezone = yield select(getTimezone);
    const filterKeyStatus = yield select(
      securitySelectors.getSecurityFilterKey,
    );
    const forceRefresh = yield select(securitySelectors.getForceRefresh);
    const getSecurityTaskList = yield select(
      securitySelectors.getSecurityTaskList,
    );
    // if firebase sends a notification or securityList is empty or filter key is pressed
    if (
      isEmpty(getSecurityTaskList) ||
      forceRefresh ||
      filterKeyStatus ||
      (from === 0 && !isEmpty(getSecurityTaskList)) ||
      (from > 0 && !isEmpty(getSecurityTaskList))
    ) {
      const { data } = yield call(API.fetchTaskList, {
        ...filters,
        from,
        timezone,
      });

      if (data.data) {
        const { meta } = data;

        yield put(
          securityGridTableActions.setGridList({
            list: data.data,
            totalEntries: meta.total,
          }),
        );
      }
    }
    if (filterKeyStatus) {
      yield put(securityFilterActions.setFilterKey(false));
    }
    if (forceRefresh) {
      yield put(securityGridTableActions.setForceRefresh(false));
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* createSecurityTaskSaga({ payload }) {
  const { data: taskData, rootTaskId } = payload;
  const assigneeName = taskData.tasks[0].assigneeFullName;
  try {
    yield put(setLoading(true));
    const { data } = yield call(createSimpleTask, {
      data: taskData,
      rootTaskId,
    });

    if (data) {
      const detailPanelActive = yield select(
        securitySelectors.getGridRowDetailsTransformed,
      );

      if (!isEmpty(detailPanelActive)) {
        yield call(fetchSecurityTaskDetailsSaga, {
          payload: {
            id: detailPanelActive.others.id.value,
            dummyTaskData: { id: detailPanelActive.others.id.value },
          },
        });
      }

      const isModelOpen = yield select(modalState);
      if (isModelOpen[securityNS]) {
        yield put(toggleModal(securityNS));
      }
    }

    yield put(securityFilterActions.setFilterKey(false));

    yield put(securityGridTableActions.setForceRefresh(false));

    if (assigneeName && assigneeName !== DB_NULL) {
      yield put(
        showSnackbar({
          typeOfSnackbar: 'success',
          message: 'Message sent',
        }),
      );
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchSecurityTaskDetailsSaga({ payload }) {
  const { id, dummyTaskData } = payload;
  try {
    yield put(setLoading(true));
    const { data } = yield call(API.fetchTaskDetails, { id });

    if (data.data) {
      // eslint-disable-next-line prefer-const
      let { tasks, joins, surfaces } = data.data;

      // if the surface id is set to null then dont convert surfaceU and surfacev
      joins = convertPercentageToimageCordinates(joins, surfaces);
      tasks = convertPercentageToimageCordinates(tasks, surfaces);
      if (Array.isArray(tasks) && tasks.length > 0) {
        data.data['tasks'] = tasks;
        data.data['joins'] = joins;
        //call the global Options for users, sites, business clients
        yield call(getGlobalUsers);
        yield call(getGlobalListSaga, {
          category: 'sites',
        });
        yield call(getGlobalListSaga, {
          category: 'businessClients',
        });

        yield put(
          securityGridTableActions.setGridRowDetails({
            details: { ...data.data, rootTaskId: id },
            dummyRowData: dummyTaskData,
          }),
        );
      }
    }

    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchSecurityTaskDetailsPdfSaga({ payload }) {
  try {
    yield put(setLoading(true));
    const { id } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchCleanerTaskDetailsPdf, {
      id,
      timezone,
      language: language.toUpperCase(),
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
    yield put(setLoading(false));
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}

export function* fetchSecurityTaskListPdfSaga({ payload }) {
  try {
    const { filters } = payload;
    const timezone = yield select(getTimezone);
    const language = yield select(getUserSelectedLanguage);
    const { data } = yield call(API.fetchCleanerTaskListPdf, {
      ...filters,
      language: language.toUpperCase(),
      timezone,
    });
    if (data.data) {
      const { url } = data.data;
      window.open(url, '_blank');
    }
  } catch (err) {
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* endSecurityTaskSaga({ payload }) {
  const { data: taskData } = payload;
  try {
    yield put(setLoading(true));
    const { tasks } = taskData;
    const transformedTasks = yield all(
      tasks.map(task => call(uploadEvidences, task)),
    );

    if (transformedTasks) {
      const { data } = yield call(API.taskUpload, {
        data: {
          ...taskData,
          tasks: transformedTasks,
        },
      });

      if (data) {
        const detailPanelActive = yield select(
          securitySelectors.getGridRowDetailsTransformed,
        );

        if (!isEmpty(detailPanelActive)) {
          yield call(fetchSecurityTaskDetailsSaga, {
            payload: {
              id: detailPanelActive.others.id.value,
              dummyTaskData: { id: detailPanelActive.others.id.value },
            },
          });
        }

        const isModelOpen = yield select(modalState);
        if (isModelOpen[PAGE_NAME.SECURITY]) {
          yield put(toggleModal(PAGE_NAME.SECURITY));
        }
      }

      const filterKeyStatus = yield select(
        securitySelectors.getSecurityFilterKey,
      );
      const forceRefresh = yield select(securitySelectors.getForceRefresh);
      if (filterKeyStatus) {
        yield put(securityFilterActions.setFilterKey(false));
      }
      if (forceRefresh) {
        yield put(securityGridTableActions.setForceRefresh(false));
      }

      const status = data.data.tasks[0]?.status;
      const taskName = data.data.tasks[0]?.name;
      yield put(
        showSnackbar({
          typeOfSnackbar: snackbarTaskStatusColor(status),
          message: translatedPerformTaskStatus('perform_task_status_message', {
            taskstatus: t(TASK_STATUS[status].name),
            taskname: taskName,
          }),
        }),
      );

      yield put(setLoading(false));
    }
  } catch (err) {
    yield put(setLoading(false));
    yield put(
      showSnackbar({
        typeOfSnackbar: 'error',
        message: errorHandler(err),
      }),
    );
  }
}
export function* securitySagas() {
  yield takeLatest(
    securityGridTableActions.fetchGridList,
    fetchSecurityListSaga,
  );
  yield takeLatest(
    securityGridTableActions.createRowEntry,
    createSecurityTaskSaga,
  );
  yield takeLatest(
    securityGridTableActions.updateRowEntry,
    createSecurityTaskSaga,
  );
  yield takeLatest(
    securityGridTableActions.fetchGridRowDetails,
    fetchSecurityTaskDetailsSaga,
  );
  yield takeLatest(
    securityGridTableActions.fetchGridRowDetailsPdf,
    fetchSecurityTaskDetailsPdfSaga,
  );
  yield takeLatest(
    securityGridTableActions.fetchGridListPdf,
    fetchSecurityTaskListPdfSaga,
  );
  yield takeLatest(securityTaskPageActions.startTask, createSecurityTaskSaga);
  yield takeLatest(securityTaskPageActions.endTask, endSecurityTaskSaga);
}
